import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { connect, useSelector } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { compose } from 'recompose';
import Input from '../../../common/components/Input/Input';
import {
  InjectedSafeTimeout,
  withSafeTimeout,
} from '../../../common/hocs/withSafeTimeout';
import AttributeModel from '@mm/state/model/AttributeModel';
import { StoreState } from '@mm/state/store-interfaces';
import './AttributeContainingTextInput.pcss';
import { nameAttributes } from '@mm/state/helpers/filterable-select-helpers';
import classnames from 'classnames';

export type ComponentInputElementTypes = HTMLInputElement | HTMLTextAreaElement;
type OuterProps = {
  value: string | undefined;
  onChange: ({
    target: { value: string },
  }: ChangeEvent<HTMLTextAreaElement> | any) => void;
  attributes?: AttributeModel[];
  useStoreAttributes?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  inputRef?: (ref: ComponentInputElementTypes) => void;
  autosize?: boolean;
  multiline?: boolean;
  className?: string;
  focusOnMount?: boolean;
  placeholder?: string;
  draggable?: boolean;
  disabled?: boolean;
  maxLength?: number;
  type?: string;
  showOnlyNameAttributes?: boolean;
  enabledSuggestions?: boolean;
  multilineMaxRows?: number;
  multilineMinRows?: number;
  isAutomationsSlideout?: boolean;
};

type StoreProps = ReturnType<typeof mapStateToProps>;
type InnerProps = StoreProps & InjectedSafeTimeout;
type Props = InnerProps & OuterProps;

const mapStateToProps = (state: StoreState) => ({
  storeAttributes: state.attribute.entities,
});

const enhance = compose<InnerProps, OuterProps>(
  withSafeTimeout,
  connect(mapStateToProps),
);

export const AttributeContainingTextInputComponent: React.FC<Props> = props => {
  const {
    placeholder,
    onChange,
    onFocus,
    className,
    multiline,
    safeTimeout,
    focusOnMount,
    autosize,
    draggable,
    disabled,
    maxLength,
    type,
    children,
    showOnlyNameAttributes = false,
    enabledSuggestions = true,
    multilineMaxRows,
    multilineMinRows,
    isAutomationsSlideout,
    useStoreAttributes,
    value: propsValue,
    attributes: propsAttributes,
    onBlur,
    inputRef: propsInputRef,
  } = props;

  const value = propsValue || '';
  const initialAttributes = useStoreAttributes
    ? props.storeAttributes
    : propsAttributes || [];

  const getNameAttributes = () =>
    initialAttributes.reduce((acc, currAttr) => {
      if (
        nameAttributes.some(nameAttribute => nameAttribute === currAttr.name)
      ) {
        return [
          ...acc,
          {
            ...currAttr,
            name: `user_${currAttr.name.split(' ').join('_')}`,
          },
        ];
      }
      return acc;
    }, []);

  const attributes = showOnlyNameAttributes
    ? getNameAttributes()
    : initialAttributes;

  const inputRef = useRef<ComponentInputElementTypes>();
  const setInputRef = ref => {
    if (ref && inputRef.current !== ref) {
      inputRef.current = ref;
      if (propsInputRef) {
        propsInputRef(ref);
        if (focusOnMount && inputRef && inputRef.current) {
          inputRef.current.focus();
        }
      }
    }
  };

  const cursorPosition = inputRef.current && inputRef.current.selectionStart;

  const [attributeNameFilter, setAttributeNameFilter] = useState('');
  // This is set TRUE when the user types a '{' brace.
  const [braceActivated, setBraceActivated] = useState(false);
  // This is somewhat complex logic to figure out two things:
  //  1. Should we show suggestions?
  //  2. What should we filter the list of suggestions by?
  const showSuggestions = useMemo(() => {
    if (
      !enabledSuggestions ||
      !braceActivated ||
      !cursorPosition ||
      value.length < 2
    ) {
      return false;
    }
    const searchText = value.substr(0, cursorPosition);
    const parts = searchText.split('{{');
    if (parts.length <= 1) {
      return false;
    }
    const foundAttributeNameFilter = parts.pop() as string;

    // If the filter contains any '}', we've stepped into a preceeding attribute.
    if (foundAttributeNameFilter.indexOf('}') >= 0) {
      return false;
    }

    // Updated the filter only if it's changed (to prevent update loop)
    if (foundAttributeNameFilter !== attributeNameFilter) {
      setAttributeNameFilter(foundAttributeNameFilter);
    }
    return true;
  }, [braceActivated, cursorPosition, value]);

  const suggestions = useMemo(
    () =>
      attributes.filter(a => {
        const nameToUse = a.friendlyName ? a.friendlyName : a.name;
        return nameToUse
          .toLowerCase()
          .startsWith(attributeNameFilter.toLowerCase());
      }),
    [attributes, attributeNameFilter],
  );

  const onSuggestionSelected = (suggestion: AttributeModel) => {
    const pos = cursorPosition || value.length;
    const text = `${suggestion.name}}}`;
    const newValue =
      value.slice(0, pos - attributeNameFilter.length) +
      text +
      value.slice(pos);
    onChange({ target: { value: newValue } });

    setBraceActivated(false);
    setAttributeNameFilter('');

    // Move cursor to position right after inserted attribute.
    safeTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.selectionStart = pos + text.length;
        el.selectionEnd = pos + text.length;
        el.scrollLeft =
          ((el.scrollWidth - el.clientWidth) * (pos + text.length)) /
          value.length;
      }
    }, 10);
  };

  const onBlurLocal = () => {
    setBraceActivated(false);
    onBlur?.();
  };

  const onKeyDown =
    (inputProps: Autosuggest.InputProps<AttributeModel>) =>
    (e: React.KeyboardEvent) => {
      const blockEventIfSuggestionsShown = () => {
        if (showSuggestions) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      switch (e.key) {
        case '{':
          blockEventIfSuggestionsShown();
          setBraceActivated(true);
          setAttributeNameFilter('');
          break;
        case 'Escape':
          setBraceActivated(false);
          setAttributeNameFilter('');

          // Autosuggest will clear the input if Escape is pressed,
          // so we don't send it this event.
          return;
        case 'Tab':
          blockEventIfSuggestionsShown();

          // Trick Autosuggest into thinking Tab is Enter.
          e.key = 'Enter';
          e.keyCode = 13;
          break;
        case 'Enter':
        case 'Home':
        case 'End':
        case 'PageUp':
        case 'PageDown':
        case 'ArrowLeft':
        case 'ArrowRight':
          blockEventIfSuggestionsShown();
          break;
        default:
          break;
      }
      inputProps.onKeyDown && inputProps.onKeyDown(e);
    };

  const renderInputComponent = (
    inputProps: Autosuggest.InputProps<AttributeModel>,
  ) => {
    const setBothRefs = ref => {
      inputProps.ref(ref);
      setInputRef(ref);
    };

    if (multiline && autosize === false) {
      return (
        <textarea
          {...inputProps}
          // The typing of inputProps.onChange isn't quite right.
          onChange={inputProps.onChange as any}
          ref={setBothRefs}
          onKeyDown={onKeyDown(inputProps)}
        />
      );
    }

    return multiline ? (
      <TextareaAutosize
        {...inputProps}
        disabled={disabled}
        draggable={draggable}
        ref={() => {}}
        inputRef={setBothRefs}
        onKeyDown={onKeyDown(inputProps)}
        maxRows={multilineMaxRows}
        minRows={multilineMinRows}
      />
    ) : (
      <Input
        {...inputProps}
        inputRef={setBothRefs}
        onKeyDown={onKeyDown(inputProps)}
        disabled={disabled}
      />
    );
  };

  const renderSuggestion = (
    suggestion: AttributeModel,
    isHighlighted: boolean,
  ) => (
    <div
      style={{
        background: isHighlighted ? '#0c7cd5' : 'white',
        color: isHighlighted ? 'white' : 'inherit',
      }}
    >
      {`{{${
        suggestion.friendlyName ? suggestion.friendlyName : suggestion.name
      }}}`}
    </div>
  );

  return (
    <div
      className={classnames('attribute-containing-text-input', className, {
        'attribute-containing-text-input__automations-slideout':
          isAutomationsSlideout,
      })}
    >
      <Autosuggest
        inputProps={{
          type,
          value,
          placeholder,
          onFocus,
          onBlur: onBlurLocal,
          maxLength,
          onChange: (e, { newValue }) => {
            if (newValue !== undefined) {
              onChange({ target: { value: newValue } });
            }
          },
        }}
        // This disables functionality that we customized
        onSuggestionsFetchRequested={() => {}}
        onSuggestionsClearRequested={() => {}}
        getSuggestionValue={s => undefined as any}
        /////////////
        suggestions={showSuggestions ? suggestions : []}
        alwaysRenderSuggestions={false || showSuggestions}
        onSuggestionSelected={(e, { suggestion }) =>
          onSuggestionSelected(suggestion)
        }
        renderInputComponent={renderInputComponent}
        renderSuggestion={(suggestion, { isHighlighted }) =>
          renderSuggestion(suggestion, isHighlighted)
        }
      />
      {children}
    </div>
  );
};

export default enhance(AttributeContainingTextInputComponent);
